import { useEffect, useMemo, useRef, useState } from "react";

type Schema<T> = Partial<Record<keyof T, (value: string) => string>>;

export function useFormValidation<T extends Record<string, string>>(
  schema: Schema<T>
) {
  const emptyErrors = useMemo(() => {
    return (Object.keys(schema) as (keyof T)[]).reduce((acc, key) => {
      acc[key as keyof T] = "";
      return acc;
    }, {} as Record<keyof T, string>);
  }, [schema]);

  const [errors, setErrors] = useState<Record<keyof T, string>>(emptyErrors);

  const prevSchemaKeys = useMemo(() => Object.keys(schema).join(","), [schema]);
  const prevKeysRef = useRef<string>("");
  useEffect(() => {
    if (prevKeysRef.current !== prevSchemaKeys) {
      setErrors(emptyErrors);
      prevKeysRef.current = prevSchemaKeys;
    }
  }, [prevSchemaKeys, emptyErrors]);

  const validateForm = (formData: T): boolean => {
    const tempErrors = { ...emptyErrors };

    (Object.keys(schema) as (keyof T)[]).forEach((key) => {
      const validator = schema[key];
      if (validator) {
        tempErrors[key] = validator(formData[key] ?? "");
      }
    });

    setErrors(tempErrors);
    return Object.values(tempErrors).every((error) => !error);
  };

  const resetErrors = () => setErrors(emptyErrors);

  return { errors, validateForm, resetErrors };
}